import React, { useCallback, useState } from 'react';
import { Tractor, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import FarmParametersSection from './FarmParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import CapitalGrantsSection from './CapitalGrantsSection';
import OperationalSubsidiesSection from './OperationalSubsidiesSection';
import LoanSubsidiesSection from './LoanSubsidiesSection';
import ExportSupportSection from './ExportSupportSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { formatCurrency } from '../../../../../utils/formatting';
import { calculateDateDifference } from '../../../../../utils/calculations/date';

/**
 * Компонент основной формы расчета сельскохозяйственных субсидий
 */
const CalculationForm = ({
  formData,
  setFormData,
  errors,
  setErrors,
  isCalculating,
  setIsCalculating,
  results,
  setResults,
  schedule,
  setSchedule,
  farmTypes,
  organizationTypes,
  regions,
  capitalGrantPurposes,
  operationalSubsidyTypes,
  saveCalculation,
  calculateSubsidySchedule
}) => {
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };

  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем количество сотрудников автоматически при изменении типа фермы
      if (name === 'farmType') {
        const selectedFarmType = farmTypes.find(type => type.id === fieldValue);
        if (selectedFarmType) {
          newState.employeeCount = selectedFarmType.defaultEmployeeCount.toString();
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeCapitalGrants' && !checked) {
        newState.capitalGrantsAmount = '1000000';
        newState.capitalGrantsPurpose = 'equipment';
      }

      if (name === 'includeOperationalSubsidies' && !checked) {
        newState.operationalSubsidiesAmount = '500000';
        newState.operationalSubsidiesType = 'seeds';
      }

      if (name === 'includeLoanSubsidies' && !checked) {
        newState.loanAmount = '3000000';
        newState.loanTerm = '5';
        newState.loanRate = '12';
      }

      if (name === 'includeExportSupport' && !checked) {
        newState.exportRevenue = '2000000';
        newState.exportCountries = 'china,belarus,kazakhstan';
      }

      // Автоматически включаем экспортную активность при выборе поддержки экспорта
      if (name === 'includeExportSupport' && checked) {
        newState.isExporter = true;
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [farmTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      farmSize, farmType, regionCode, organizationType, farmingExperience, annualRevenue, employeeCount,
      programDuration, startDate, 
      includeCapitalGrants, capitalGrantsAmount, capitalGrantsPurpose,
      includeOperationalSubsidies, operationalSubsidiesAmount, operationalSubsidiesType,
      includeLoanSubsidies, loanAmount, loanTerm, loanRate,
      includeExportSupport, exportRevenue, exportCountries
    } = formData;

    // Парсинг числовых значений
    const numFarmSize = parseFloatOrDefault(farmSize, -1);
    const numFarmingExperience = parseFloatOrDefault(farmingExperience, -1);
    const numAnnualRevenue = parseFloatOrDefault(annualRevenue, -1);
    const numEmployeeCount = parseFloatOrDefault(employeeCount, -1);
    const numProgramDuration = parseFloatOrDefault(programDuration, -1);

    // Валидация базовых полей
    if (numFarmSize <= 0) newErrors.farmSize = 'Размер должен быть положительным числом.';
    if (!newErrors.farmSize && numFarmSize > 100000) newErrors.farmSize = 'Максимальный размер 100 тыс. га.';
    
    if (numFarmingExperience < 0) newErrors.farmingExperience = 'Опыт не может быть отрицательным.';
    if (!newErrors.farmingExperience && numFarmingExperience > 100) newErrors.farmingExperience = 'Максимальное значение 100 лет.';
    
    if (numAnnualRevenue < 0) newErrors.annualRevenue = 'Выручка не может быть отрицательной.';
    if (!newErrors.annualRevenue && numAnnualRevenue > 10000000000) newErrors.annualRevenue = 'Максимальное значение 10 млрд ₽.';
    
    if (numEmployeeCount < 0) newErrors.employeeCount = 'Количество сотрудников не может быть отрицательным.';
    if (!isInteger(employeeCount) || numEmployeeCount <= 0) newErrors.employeeCount = 'Введите целое положительное число.';
    
    if (numProgramDuration <= 0) newErrors.programDuration = 'Срок должен быть положительным числом.';
    if (!newErrors.programDuration && numProgramDuration > 10) newErrors.programDuration = 'Максимальный срок 10 лет.';

    // Проверка даты начала программы
    if (!startDate) newErrors.startDate = 'Укажите дату начала программы.';
    try {
      const dateObject = new Date(startDate);
      if (isNaN(dateObject.getTime())) {
        newErrors.startDate = 'Некорректная дата.';
      }
    } catch (e) {
      newErrors.startDate = 'Некорректная дата.';
    }

    // Валидация полей для капитальных грантов
    if (includeCapitalGrants) {
      const numCapitalGrantsAmount = parseFloatOrDefault(capitalGrantsAmount, -1);
      if (numCapitalGrantsAmount <= 0) newErrors.capitalGrantsAmount = 'Сумма должна быть положительной.';
      if (!newErrors.capitalGrantsAmount && numCapitalGrantsAmount > 50000000) {
        newErrors.capitalGrantsAmount = 'Максимальная сумма 50 млн ₽.';
      }
      if (!capitalGrantsPurpose) newErrors.capitalGrantsPurpose = "Выберите цель гранта.";
    }

    // Валидация полей для операционных субсидий
    if (includeOperationalSubsidies) {
      const numOperationalSubsidiesAmount = parseFloatOrDefault(operationalSubsidiesAmount, -1);
      if (numOperationalSubsidiesAmount <= 0) newErrors.operationalSubsidiesAmount = 'Сумма должна быть положительной.';
      if (!newErrors.operationalSubsidiesAmount && numOperationalSubsidiesAmount > 20000000) {
        newErrors.operationalSubsidiesAmount = 'Максимальная сумма 20 млн ₽.';
      }
      if (!operationalSubsidiesType) newErrors.operationalSubsidiesType = "Выберите тип субсидии.";
    }

    // Валидация полей для кредитных субсидий
    if (includeLoanSubsidies) {
      const numLoanAmount = parseFloatOrDefault(loanAmount, -1);
      const numLoanTerm = parseFloatOrDefault(loanTerm, -1);
      const numLoanRate = parseFloatOrDefault(loanRate, -1);
      
      if (numLoanAmount <= 0) newErrors.loanAmount = 'Сумма должна быть положительной.';
      if (!newErrors.loanAmount && numLoanAmount > 100000000) {
        newErrors.loanAmount = 'Максимальная сумма 100 млн ₽.';
      }
      
      if (numLoanTerm <= 0) newErrors.loanTerm = 'Срок должен быть положительным числом.';
      if (!newErrors.loanTerm && numLoanTerm > 15) newErrors.loanTerm = 'Максимальный срок 15 лет.';
      
      if (numLoanRate <= 0) newErrors.loanRate = 'Ставка должна быть положительной.';
      if (!newErrors.loanRate && numLoanRate > 30) newErrors.loanRate = 'Ставка не может быть более 30%.';
    }

    // Валидация полей для экспортной поддержки
    if (includeExportSupport) {
      const numExportRevenue = parseFloatOrDefault(exportRevenue, -1);
      if (numExportRevenue <= 0) newErrors.exportRevenue = 'Сумма должна быть положительной.';
      if (!newErrors.exportRevenue && numExportRevenue > 50000000) {
        newErrors.exportRevenue = 'Максимальная сумма 50 млн ₽.';
      }
      if (!exportCountries || exportCountries.trim() === '') newErrors.exportCountries = "Укажите страны экспорта.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета графика выплат субсидий
  const calculateSubsidies = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const farmSize = parseFloatOrDefault(formData.farmSize);
        const farmingExperience = parseFloatOrDefault(formData.farmingExperience);
        const annualRevenue = parseFloatOrDefault(formData.annualRevenue);
        const employeeCount = parseIntOrDefault(formData.employeeCount);
        const programDuration = parseFloatOrDefault(formData.programDuration);
        const selectedRegion = regions.find(r => r.code === formData.regionCode) || { subsidyCoefficient: 1.0 };
        const organizationType = organizationTypes.find(o => o.id === formData.organizationType) || { maxSubsidyPercent: 70 };
        
        // --- Инициализируем переменные для расчета ---
        // Базовый коэффициент, зависящий от размера хозяйства
        let baseSizeCoefficient = 1.0;
        if (farmSize <= 10) baseSizeCoefficient = 1.2; // Для малых хозяйств
        else if (farmSize <= 100) baseSizeCoefficient = 1.1; // Для средних хозяйств
        else if (farmSize <= 1000) baseSizeCoefficient = 1.0; // Для крупных хозяйств
        else baseSizeCoefficient = 0.9; // Для очень крупных хозяйств
        
        // Коэффициент опыта
        const experienceCoefficient = Math.min(1.0 + (farmingExperience * 0.02), 1.5);
        
        // Коэффициент дополнительных критериев
        let additionalCriteriaCoefficient = 1.0;
        if (formData.isEcoFriendly) additionalCriteriaCoefficient += 0.15;
        if (formData.isInnovative) additionalCriteriaCoefficient += 0.2;
        if (formData.isExporter) additionalCriteriaCoefficient += 0.1;
        if (formData.hasInsurance) additionalCriteriaCoefficient += 0.05;
        if (formData.hasSocialPrograms) additionalCriteriaCoefficient += 0.05;
        
        // Расчет субсидий для разных программ
        let capitalGrantAmount = 0;
        let operationalSubsidyAmount = 0;
        let loanSubsidyAmount = 0;
        let exportSupportAmount = 0;
        let totalSubsidyAmount = 0;

        // 1. Расчет капитальных грантов
        if (formData.includeCapitalGrants) {
          const requestedAmount = parseFloatOrDefault(formData.capitalGrantsAmount);
          const purpose = capitalGrantPurposes.find(p => p.id === formData.capitalGrantsPurpose) || { fundingPercent: 50 };
          
          // Расчет предельного размера субсидии
          const maxGrantPercent = Math.min(purpose.fundingPercent, organizationType.maxSubsidyPercent);
          
          // Расчет максимального размера субсидии на основе годовой выручки
          const maxBasedOnRevenue = annualRevenue * 0.8;
          
          // Применение различных коэффициентов
          const calculatedAmount = Math.min(
            requestedAmount, 
            maxBasedOnRevenue
          ) * (maxGrantPercent / 100) * baseSizeCoefficient * selectedRegion.subsidyCoefficient * experienceCoefficient * additionalCriteriaCoefficient;
          
          capitalGrantAmount = Math.min(30000000, calculatedAmount); // Максимум 30 млн рублей
        }

        // 2. Расчет операционных субсидий
        if (formData.includeOperationalSubsidies) {
          const requestedAmount = parseFloatOrDefault(formData.operationalSubsidiesAmount);
          const subsidyType = operationalSubsidyTypes.find(t => t.id === formData.operationalSubsidiesType) || { fundingPercent: 40 };
          
          // Расчет предельного размера субсидии
          const maxSubsidyPercent = Math.min(subsidyType.fundingPercent, organizationType.maxSubsidyPercent);
          
          // Расчет максимального размера субсидии на основе размера хозяйства
          const maxBasedOnSize = farmSize * 50000; // 50000 рублей на гектар
          
          // Применение различных коэффициентов
          const calculatedAmount = Math.min(
            requestedAmount, 
            maxBasedOnSize
          ) * (maxSubsidyPercent / 100) * selectedRegion.subsidyCoefficient * additionalCriteriaCoefficient;
          
          operationalSubsidyAmount = Math.min(10000000, calculatedAmount); // Максимум 10 млн рублей
        }

        // 3. Расчет субсидий на кредит
        if (formData.includeLoanSubsidies) {
          const loanAmount = parseFloatOrDefault(formData.loanAmount);
          const loanTerm = parseFloatOrDefault(formData.loanTerm);
          const loanRate = parseFloatOrDefault(formData.loanRate);
          
          // Базовая ставка субсидирования
          let subsidyRatePercent = 0;
          if (loanRate <= 5) subsidyRatePercent = 60; // Если ставка до 5% - субсидируем 60% ставки
          else if (loanRate <= 10) subsidyRatePercent = 80; // Если ставка до 10% - субсидируем 80% ставки
          else subsidyRatePercent = 90; // Если ставка выше 10% - субсидируем 90% ставки
          
          // Учет региональных особенностей
          if (selectedRegion.subsidyCoefficient > 1.0) {
            subsidyRatePercent += 5; // Дополнительно 5% для приоритетных регионов
          }
          
          // Максимальный размер субсидии - не более 12% от суммы кредита в год
          const maxYearlySubsidy = loanAmount * 0.12;
          
          // Расчет общей суммы субсидии на весь срок кредита
          const calculatedAmount = Math.min(
            (loanAmount * (loanRate / 100) * (subsidyRatePercent / 100) * loanTerm), // Расчет по формуле
            (maxYearlySubsidy * loanTerm) // Ограничение максимальной суммы
          ) * experienceCoefficient * additionalCriteriaCoefficient;
          
          loanSubsidyAmount = Math.min(15000000, calculatedAmount); // Максимум 15 млн рублей
        }

        // 4. Расчет поддержки экспорта
        if (formData.includeExportSupport && formData.isExporter) {
          const exportRevenue = parseFloatOrDefault(formData.exportRevenue);
          const exportCountries = formData.exportCountries.split(',').map(c => c.trim()).filter(c => c);
          
          // Базовый процент поддержки
          let exportSupportPercent = 15;
          
          // Дополнительный процент за количество стран экспорта
          if (exportCountries.length >= 5) exportSupportPercent += 10;
          else if (exportCountries.length >= 3) exportSupportPercent += 5;
          
          // Расчет суммы поддержки
          const calculatedAmount = (exportRevenue * (exportSupportPercent / 100)) * 
            selectedRegion.subsidyCoefficient * 
            additionalCriteriaCoefficient;
          
          exportSupportAmount = Math.min(8000000, calculatedAmount); // Максимум 8 млн рублей
        }

        // Общая сумма всех субсидий
        totalSubsidyAmount = capitalGrantAmount + operationalSubsidyAmount + loanSubsidyAmount + exportSupportAmount;

        // --- Готовим итоговые результаты ---
        const finalResults = {
          // Базовые параметры и коэффициенты
          farmSize: farmSize,
          farmType: formData.farmType,
          region: selectedRegion,
          organizationType: organizationType,
          baseSizeCoefficient: baseSizeCoefficient,
          experienceCoefficient: experienceCoefficient,
          additionalCriteriaCoefficient: additionalCriteriaCoefficient,
          
          // Результаты расчетов по программам
          capitalGrantAmount: capitalGrantAmount,
          operationalSubsidyAmount: operationalSubsidyAmount,
          loanSubsidyAmount: loanSubsidyAmount,
          exportSupportAmount: exportSupportAmount,
          totalSubsidyAmount: totalSubsidyAmount,
          
          // Расчет на гектар
          subsidyPerHectare: totalSubsidyAmount / farmSize,
          
          // Прочие параметры
          programDuration: programDuration,
          startDate: new Date(formData.startDate),
          endDate: new Date(new Date(formData.startDate).setFullYear(new Date(formData.startDate).getFullYear() + programDuration))
        };

        setResults(finalResults);
        
        // Генерируем график выплат
        calculateSubsidySchedule(formData, finalResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({
          general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.`
        });
        setResults(null);
        setSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setSchedule, setIsCalculating, calculateSubsidySchedule, regions, organizationTypes, capitalGrantPurposes, operationalSubsidyTypes]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      farmSize: '100',
      farmType: 'crop',
      regionCode: '77',
      organizationType: 'individual',
      farmingExperience: '3',
      annualRevenue: '5000000',
      employeeCount: '5',
      
      isEcoFriendly: false,
      isInnovative: false,
      isExporter: false,
      hasInsurance: false,
      hasSocialPrograms: false,
      
      includeCapitalGrants: true,
      capitalGrantsAmount: '1000000',
      capitalGrantsPurpose: 'equipment',
      
      includeOperationalSubsidies: false,
      operationalSubsidiesAmount: '500000',
      operationalSubsidiesType: 'seeds',
      
      includeLoanSubsidies: false,
      loanAmount: '3000000',
      loanTerm: '5',
      loanRate: '12',
      
      includeExportSupport: false,
      exportRevenue: '2000000',
      exportCountries: 'china,belarus,kazakhstan',
      
      startDate: new Date().toISOString().split('T')[0],
      programDuration: '3',
    });
    setResults(null);
    setSchedule([]);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт графика выплат в CSV
  const exportToCSV = () => {
    if (!schedule || schedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = ['Год', 'Квартал', 'Дата', 'Сумма', 'Описание', 'Статус'];

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');
    const formatDate = (date) => date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    
    const statusTranslations = {
      'approved': 'Одобрено',
      'processing': 'В обработке', 
      'planned': 'Запланировано'
    };

    const rows = schedule.map(item => {
      return [
        item.year,
        item.quarter,
        formatDate(item.date),
        formatCsvNumber(item.amount),
        item.description,
        statusTranslations[item.paymentStatus] || item.paymentStatus
      ].join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 'ИТОГО:', 
      '',
      formatCsvNumber(results.totalSubsidyAmount),
      `Общая сумма субсидий на ${results.programDuration} ${results.programDuration === 1 ? 'год' : results.programDuration < 5 ? 'года' : 'лет'}`,
      ''
    ].join(';');
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Размер фермы:', `${results.farmSize} га`],
      ['Тип хозяйства:', getFarmTypeName(formData.farmType)],
      ['Регион:', regions.find(r => r.code === formData.regionCode)?.name || 'Не указан'],
      ['Тип организации:', organizationTypes.find(o => o.id === formData.organizationType)?.name || 'Не указан'],
      ['Дата начала программы:', formatDate(new Date(formData.startDate))],
      ['Период программы:', `${results.programDuration} ${results.programDuration === 1 ? 'год' : results.programDuration < 5 ? 'года' : 'лет'}`],
      ['Общая сумма субсидий:', formatCsvNumber(results.totalSubsidyAmount)],
      ['Субсидия на гектар:', formatCsvNumber(results.subsidyPerHectare)],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'график_субсидий.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Вспомогательная функция для получения названия типа хозяйства
  const getFarmTypeName = (typeId) => {
    const farmType = farmTypes.find(type => type.id === typeId);
    return farmType ? farmType.name : 'Неизвестный тип';
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateSubsidies();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Tractor className="w-6 h-6 text-emerald-500" />
          Параметры сельскохозяйственного предприятия
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры фермы */}
        <FarmParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          farmTypes={farmTypes}
          regions={regions}
          organizationTypes={organizationTypes}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Капитальные гранты */}
        <CapitalGrantsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          capitalGrantPurposes={capitalGrantPurposes}
        />

        {/* Операционные субсидии */}
        <OperationalSubsidiesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          operationalSubsidyTypes={operationalSubsidyTypes}
        />

        {/* Субсидирование кредитов */}
        <LoanSubsidiesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Поддержка экспорта */}
        <ExportSupportSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать субсидии'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {schedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;